/**
 * Клиент API обратной связи (/api/feedback/…): токен пользователя — из lib/auth.
 */
import { getTokens } from "@/lib/auth"
import { uploadViaV3 } from "@/modules/bugfix-agent/lib/client/v3-upload"
import type { Attachment, Feedback, FeedbackKind, FeedbackListItem, FeedbackRating, FeedbackStats, FeedbackStatus, MyRating, Person } from "../shared/types"

export class FeedbackApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

function token(): string {
  const t = getTokens()?.accessToken
  if (!t) throw new FeedbackApiError(401, "Нет авторизации — войдите заново")
  return t
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/feedback${path}`, {
    ...init,
    cache: "no-store",
    headers: { Authorization: `Bearer ${token()}`, ...(init.body ? { "Content-Type": "application/json" } : {}), ...init.headers },
  })
  const body = (await res.json().catch(() => ({}))) as { error?: string }
  if (!res.ok) throw new FeedbackApiError(res.status, body.error || `Ошибка сервера (HTTP ${res.status})`)
  return body as T
}

const json = (method: string, body?: unknown): RequestInit => ({ method, body: JSON.stringify(body ?? {}) })

/** Файл → API v3 из браузера, затем вложение отзыва */
export function uploadFeedbackFile(file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal): Promise<Attachment> {
  return uploadViaV3("/api/feedback/uploads", file, onProgress, signal)
}

export interface CreateFeedbackInput {
  kind: FeedbackKind
  rating?: FeedbackRating | null
  tags?: string[]
  comment?: string
  attachmentIds?: string[]
  context: unknown
  copilot?: { dialogId: string | null; messageId: string | null; dialogTitle?: string | null; messages?: unknown[]; pathLength?: number }
}

export interface ListQuery {
  kind?: FeedbackKind[]
  status?: FeedbackStatus[]
  rating?: FeedbackRating[]
  author?: number[]
  q?: string
  limit?: number
  offset?: number
}

export const feedbackApi = {
  create: (input: CreateFeedbackInput) => request<{ feedback: Feedback }>("", json("POST", input)).then((r) => r.feedback),
  update: (id: string, input: { rating?: FeedbackRating | null; tags?: string[]; comment?: string; attachmentIds?: string[] }) => request<{ feedback: Feedback }>(`/${id}`, json("PATCH", input)).then((r) => r.feedback),
  remove: (id: string) => request<{ ok: true }>(`/${id}`, { method: "DELETE" }),
  mine: (dialogId: string) => request<{ ratings: MyRating[] }>(`/mine?dialogId=${encodeURIComponent(dialogId)}`).then((r) => r.ratings),
  get: (id: string) => request<{ feedback: Feedback }>(`/${id}`).then((r) => r.feedback),
  list: (q: ListQuery = {}) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v != null && (!Array.isArray(v) || v.length) && v !== "") p.set(k, Array.isArray(v) ? v.join(",") : String(v))
    const qs = p.toString()
    return request<{ items: FeedbackListItem[]; total: number }>(qs ? `?${qs}` : "")
  },
  stats: () => request<{ stats: FeedbackStats & { authors: Person[] } }>("/stats").then((r) => r.stats),
  setStatus: (id: string, status: FeedbackStatus) => request<{ feedback: Feedback }>(`/${id}/status`, json("POST", { status })).then((r) => r.feedback),
  addNote: (id: string, text: string) => request<{ feedback: Feedback }>(`/${id}/notes`, json("POST", { text })).then((r) => r.feedback),
  toAgent: (id: string) => request<{ feedback: Feedback; task: { id: string; number: number; title: string; error: string | null } }>(`/${id}/bugfix`, json("POST")),
  debugCopy: (id: string) => request<{ dialogId: string; title: string }>(`/${id}/debug-copy`, json("POST")),
}
